import { useState, useMemo, memo, useEffect } from "react";
import { Box } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import CircularProgress from "../CircularProgress";
import { useAppSelector } from "../../store/hooks";
import { selectChartsData } from "../../features/productAnalyzer/productAnalyzer.selector";
import { selectProductAnalyzerSettings } from "../../features/settings/settings.selector";
import { ProductCharts } from "../ProductCharts";
import ChartsToolbar, { type ChartMode } from "./partials/ChartsToolbar";
import { BASIC_FILTERS, createFilters, getDefaultFilterValue } from "./lib/filters";

type TChartsProps = {
  hideOpenNew?: boolean;
};

const MemoizedProductCharts = memo(ProductCharts);

const ChartsBase = ({ hideOpenNew }: TChartsProps) => {
  const [filter, setFilter] = useState(BASIC_FILTERS[2].value);
  const [mode, setMode] = useState<ChartMode>("panning");

  const [searchParams] = useSearchParams();

  const { loadingChartsData } = useAppSelector(selectChartsData);
  const { defaultChartTimeSpan } = useAppSelector(
    selectProductAnalyzerSettings,
  );

  const asin = searchParams.get("asin");

  const FILTERS = useMemo(
    () => createFilters(defaultChartTimeSpan),
    [defaultChartTimeSpan],
  );

  // Always show 5 buttons; select default based on setting
  useEffect(() => {
    setFilter(getDefaultFilterValue(defaultChartTimeSpan));
  }, [defaultChartTimeSpan]);

  const navigate = useNavigate();
  const handleNavigateToCharts = () => {
    window.open(
      `${process.env.REACT_APP_SERVER_URL}/view-charts?asin=${asin}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  if (loadingChartsData) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="600px"
        width="100%"
      >
        <CircularProgress size="md" />
      </Box>
    );
  }

  if (!asin) {
    return null;
  }

  return (
    <Box display={"flex"} width={"100%"} flexDirection={"column"}>
      <MemoizedProductCharts interactionMode={mode} rangeFilter={filter} />
      <ChartsToolbar
        filters={FILTERS}
        selectedFilter={filter}
        onFilterChange={(filter) => setFilter(filter.value)}
        mode={mode}
        onModeChange={setMode}
        asin={asin}
        hideOpenNew={hideOpenNew}
        onOpenExternal={handleNavigateToCharts}
      />
    </Box>
  );
};

export const Charts = memo(ChartsBase);
export default Charts;
